/**
 * Pure helpers behind the Referrals page, the referral pricing fields and the
 * tags page reason filters (kept out of the components so vitest can cover
 * them without a DOM).
 */
import type {
  DecideReferralInput,
  ListPoolTagsInput,
  ReferralDecision,
  ReferralStatus,
  TagPoolStatus,
  UpdatePricingConfigInput,
} from "@/lib/api";

export const REFERRAL_STATUS_OPTIONS: readonly ReferralStatus[] = [
  "review", "registered", "verified", "credited", "awarded", "rejected", "clawed_back",
];

/** Decisions the server accepts from each status (mirrors decide-referral.ts). */
export function allowedDecisions(status: ReferralStatus): ReferralDecision[] {
  switch (status) {
    case "review": return ["approve", "reject"];
    case "registered":
    case "verified": return ["reject"];
    case "credited":
    case "awarded": return ["clawback"];
    default: return [];
  }
}

export const NOTE_MAX = 500;

export const DECISION_LABEL: Record<ReferralDecision, string> = {
  approve: "Approve",
  reject: "Reject",
  clawback: "Claw back",
};

export type DecisionBuild = { ok: true; input: DecideReferralInput } | { ok: false; error: string };

/** Reject and clawback need a note; approve's is optional. Mirrors the server's limits. */
export function buildDecision(refereeUid: string, decision: ReferralDecision, rawNote: string): DecisionBuild {
  const note = rawNote.trim();
  if (decision !== "approve" && !note) return { ok: false, error: "A note is required to reject or claw back." };
  if (note.length > NOTE_MAX) return { ok: false, error: `Note must be at most ${NOTE_MAX} characters.` };
  return { ok: true, input: { refereeUid, decision, ...(note ? { note } : {}) } };
}

/** Referral pricing fields: inclusive whole-number bounds (mirrors pricing-config.ts). */
export const REFERRAL_PRICING_FIELDS = [
  { key: "referralHoldDays", label: "Hold period (days)", min: 0, max: 30 },
  { key: "referralMaxPerReferrer24h", label: "Max referees per referrer per day", min: 1, max: 20 },
  { key: "referralMaxPerIp24h", label: "Max registrations per IP per day", min: 1, max: 50 },
  { key: "referralSignupWindowHours", label: "Signup window (hours)", min: 1, max: 168 },
] as const;
export type ReferralPricingKey = (typeof REFERRAL_PRICING_FIELDS)[number]["key"];

export type FieldBuild = { ok: true; input: UpdatePricingConfigInput } | { ok: false; error: string };

export function buildReferralPricingUpdate(key: ReferralPricingKey, raw: string): FieldBuild {
  const f = REFERRAL_PRICING_FIELDS.find((x) => x.key === key)!;
  const n = Number(raw);
  if (raw.trim() === "" || !Number.isInteger(n) || n < f.min || n > f.max) {
    return { ok: false, error: `Enter a whole number between ${f.min} and ${f.max}.` };
  }
  return { ok: true, input: { [key]: n } };
}

/** Revoked-reason sub-filters: always sent with status "revoked". */
export const REVOKED_REASON_OPTIONS = ["unsubscribed_after_find", "referral_clawback"] as const;

export function tagListFilters(
  status: "" | TagPoolStatus,
  revokedReason: string,
): Pick<ListPoolTagsInput, "status" | "revokedReason"> {
  return {
    status: status || undefined,
    // The reason only means something (and is only indexed) for revoked tags.
    revokedReason: status === "revoked" && revokedReason ? revokedReason : undefined,
  };
}

/** Human label for a pausedReason / revokedReason chip. */
export function reasonLabel(reason: string): string {
  switch (reason) {
    case "lapsed": return "lapsed";
    case "expired": return "expired";
    case "unsubscribed_after_find": return "unsubscribed after find";
    case "referral_clawback": return "referral clawback";
    default: return reason.replace(/_/g, " ");
  }
}
